import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightning, Clock, ArrowRight, Trophy } from "@phosphor-icons/react";
import { getActiveWeeklyChallenge } from "../lib/api";

/**
 * Compact retention CTA — renders only when an active weekly Transfer Desk
 * challenge exists. Designed to slot into Dashboard, Library, or any page
 * where we want to surface "this week's game" without owning the data flow.
 */
export default function WeeklyChallengeBanner({ variant = "wide" }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        getActiveWeeklyChallenge()
            .then((d) => { if (!cancelled) setData(d); })
            .catch(() => { if (!cancelled) setData({ active: false }); });
        return () => { cancelled = true; };
    }, []);

    if (!data?.active || !data?.challenge) return null;

    const c = data.challenge;
    const dur = c.duration || {};
    const auDate = data.active_until ? new Date(data.active_until) : null;
    const msLeft = auDate ? auDate.getTime() - Date.now() : 0;
    if (msLeft <= 0) return null;
    const hoursLeft = Math.max(0, Math.floor(msLeft / 3_600_000));
    const daysLeft = Math.floor(hoursLeft / 24);
    const remaining = daysLeft > 0
        ? `${daysLeft}d ${hoursLeft % 24}h left`
        : `${hoursLeft}h left`;

    const urgent = hoursLeft < 24;

    if (variant === "compact") {
        return (
            <Link
                to={`/play/market/${c.id}`}
                data-testid="weekly-banner-compact"
                className={`block border ${urgent ? "border-fa-yellow" : "border-fa-violet/50"} bg-[#0d0d0d] px-3 py-2 hover:border-fa-yellow transition-colors flex items-center gap-3`}
            >
                <Lightning size={14} weight="fill" className="text-fa-yellow flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                        This week
                    </div>
                    <div className="font-display font-black text-sm truncate">
                        {c.title}
                    </div>
                </div>
                <div className={`font-mono text-[10px] uppercase tracking-widest flex-shrink-0 ${urgent ? "text-fa-yellow" : "text-fa-mint"}`}>
                    {remaining}
                </div>
            </Link>
        );
    }

    return (
        <section
            className={`border-2 ${urgent ? "border-fa-yellow animate-pulse-slow" : "border-fa-violet"} bg-gradient-to-r from-fa-violet/15 via-[#0d0d0d] to-[#0d0d0d] p-5 md:p-6 relative overflow-hidden`}
            data-testid="weekly-banner"
        >
            <div className="grain-overlay" />
            <div className="relative flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-4 min-w-0 flex-1">
                    <div className="flex-shrink-0 w-10 h-10 bg-fa-yellow/15 border border-fa-yellow flex items-center justify-center">
                        <Lightning size={18} weight="fill" className="text-fa-yellow" />
                    </div>
                    <div className="min-w-0">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-1.5">
                            <span className="inline-block w-1.5 h-1.5 bg-fa-yellow rounded-full animate-ping" />
                            Live this week · Market Decision
                        </div>
                        <div className="font-display font-black text-xl md:text-2xl uppercase tracking-tight mt-1 truncate">
                            {c.title}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-white/50 mt-1">
                            <span className="text-fa-cream">GW {dur.fromGameweek}–{dur.toGameweek}</span>
                            <span>· {(c.players || []).length} players</span>
                            <span className={`flex items-center gap-1 ${urgent ? "text-fa-yellow" : "text-fa-mint"}`} data-testid="weekly-banner-remaining">
                                <Clock size={10} weight="fill" />
                                {remaining} {urgent ? "· last chance" : "until deadline"}
                            </span>
                        </div>
                    </div>
                </div>
                <Link
                    to={`/play/market/${c.id}`}
                    className="bg-fa-yellow text-black font-display font-black uppercase tracking-wide px-5 py-3 hover:bg-fa-yellow/90 transition-colors flex items-center gap-2 flex-shrink-0"
                    data-testid="weekly-banner-cta"
                >
                    Play now
                    <ArrowRight size={14} weight="bold" />
                </Link>
            </div>
            <Trophy size={11} weight="fill" className="absolute top-3 right-3 text-fa-yellow/30" />
        </section>
    );
}
